import * as z from "zod";
import { containsRetiredProductName } from "./product-identity.js";

export const SITE_DOCUMENT_VERSION = 1;
export const SITE_DOCUMENT_MAX_BYTES = 2 * 1024 * 1024;
export const SITE_MAX_SITES_PER_OWNER_SPACE = 50;
export const SITE_MAX_RELEASES_PER_SITE = 100;
export const SITE_INTERACTIVE_MAX_LENGTH = 512 * 1024;
const SITE_RESERVED_HANDLES = new Set([
  "www",
  "admin",
  "auth",
  "api",
  "app",
  "assets",
  "status",
  "system",
  "root",
  "mail",
  "smtp",
  "imap",
  "ftp",
  "localhost",
  "updates",
  "login",
  "signup",
  "support",
]);

function handle(max: number) {
  return z
    .string()
    .min(1)
    .max(max)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/)
    .refine((value) => !SITE_RESERVED_HANDLES.has(value), "This address is reserved");
}

// Names are explicit and immutable; never silently transliterate a display title.
// Назви явні й незмінні; видимий заголовок не транслітерується автоматично.
export const SiteSlugSchema = handle(63);
export const SiteSpaceHandleSchema = handle(32);
// Only new addresses reject retired branding; historical records remain readable.
// Лише нові адреси відхиляють старий бренд; історичні записи залишаються читабельними.
const currentProductAddress = (value: string) => !containsRetiredProductName(value);
const retiredAddressMessage =
  "Use a current product or project address / Використайте актуальну назву продукту або проєкту";
export const SiteSlugWriteSchema = SiteSlugSchema.refine(
  currentProductAddress,
  retiredAddressMessage,
);
export const SiteSpaceHandleWriteSchema = SiteSpaceHandleSchema.refine(
  currentProductAddress,
  retiredAddressMessage,
);
export const SiteTitleWriteSchema = z
  .string()
  .min(1)
  .max(180)
  .refine(currentProductAddress, retiredAddressMessage);
export const SiteVisibilitySchema = z.enum(["private", "public"]);
export type SiteVisibility = z.infer<typeof SiteVisibilitySchema>;

export const SiteCreateInput = z
  .object({
    namespaceHandle: SiteSpaceHandleWriteSchema,
    slug: SiteSlugWriteSchema,
    title: SiteTitleWriteSchema,
    creationKey: z.string().min(1).max(96),
    creatorBotId: z.string().min(1).max(96).optional(),
  })
  .strict();
export const SiteRemoveVersionInput = z
  .object({
    siteId: z.string().min(1).max(96),
    releaseId: z.string().min(1).max(96),
    expectedRevision: z.number().int().min(0),
    expectedGeneration: z.number().int().min(0),
    expectedActiveReleaseId: z.string().min(1).max(96).nullable(),
    confirm: z.literal(true),
  })
  .strict();
export const SiteActivateInput = z
  .object({
    siteId: z.string().min(1).max(96),
    releaseId: z.string().min(1).max(96),
    expectedRevision: z.number().int().min(0),
    expectedGeneration: z.number().int().min(0),
    expectedActiveReleaseId: z.string().min(1).max(96).nullable(),
    visibility: SiteVisibilitySchema,
  })
  .strict();
export const SiteSummarySchema = z.object({
  id: z.string(),
  title: z.string(),
  namespaceHandle: SiteSpaceHandleSchema,
  slug: SiteSlugSchema,
  visibility: SiteVisibilitySchema,
  lifecycle: z.enum(["active", "suspended", "archived"]),
  generation: z.number().int(),
  revision: z.number().int(),
  activeReleaseId: z.string().nullable(),
  creatorBotId: z.string().nullable(),
  url: z.string().url(),
  openUrl: z.string().url(),
  destinationReady: z.boolean().optional(),
  createdAt: z.string(),
});
export type SiteSummary = z.infer<typeof SiteSummarySchema>;
export const SiteVersionSchema = z.object({
  id: z.string(),
  siteId: z.string(),
  version: z.number().int().min(1),
  createdAt: z.string(),
  previewUrl: z.string().url(),
  deletionPending: z.boolean().optional(),
});
export type SiteVersion = z.infer<typeof SiteVersionSchema>;

export const SitePathSchema = z
  .string()
  .max(256)
  .regex(/^\/(?:[a-zA-Z0-9_-]+(?:\/[a-zA-Z0-9_-]+)*)?$/)
  .refine((value) => !value.split("/").some((part) => part.startsWith("_")), "Reserved path");

export const SitePreviewPathSchema = z
  .string()
  .max(384)
  .regex(
    /^\/_hrok\/preview\/[A-Za-z0-9_-]{1,96}(?:\/(?:[a-zA-Z0-9-][a-zA-Z0-9_-]*(?:\/[a-zA-Z0-9-][a-zA-Z0-9_-]*)*)?)?$/,
  );
export const SiteAuthReturnPathSchema = z.union([SitePathSchema, SitePreviewPathSchema]);

export const SiteHttpsUrlSchema = z
  .string()
  .min(1)
  .max(2048)
  .refine((value) => {
    if (
      value.includes("\\") ||
      [...value].some(
        (char) => /\s/.test(char) || char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127,
      )
    )
      return false;
    try {
      const url = new URL(value);
      return (
        value.startsWith("https://") &&
        url.protocol === "https:" &&
        !url.username &&
        !url.password &&
        (!url.port || url.port === "443") &&
        /^[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?$/.test(url.hostname) &&
        url.hostname
          .split(".")
          .every(
            (part) =>
              part.length > 0 && part.length <= 63 && !part.startsWith("-") && !part.endsWith("-"),
          )
      );
    } catch {
      return false;
    }
  }, "Expected an HTTPS address without credentials or control characters");

const Text = z.string().min(1).max(6000);
const Heading = z.string().min(1).max(180);
const BlockId = z.string().regex(/^[a-z][a-z0-9-]{0,47}$/);
export const SiteLinkSchema = z
  .object({
    label: z.string().min(1).max(80),
    href: z.union([
      SiteHttpsUrlSchema,
      z.string().regex(/^#[a-z][a-z0-9-]{0,47}$/),
      SitePathSchema,
    ]),
  })
  .strict();

export const SITE_MEDIA_MAX_BYTES = 50 * 1024 * 1024;
export const SiteMediaImportInput = z
  .object({
    siteId: z.string().min(1).max(96),
    artifactId: z.string().min(1).max(96),
    operationKey: z.string().min(1).max(96),
  })
  .strict();
export const SiteMediaIdSchema = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_-]{0,95}$/);
export const SiteMediaPathSchema = z.string().regex(/^\/media\/[A-Za-z0-9][A-Za-z0-9_-]{0,95}$/);
export const SiteAssetSchema = z
  .object({
    id: SiteMediaIdSchema,
    mimeType: z.enum(["image/png", "image/jpeg", "image/webp", "video/mp4", "video/webm"]),
    size: z.number().int().min(1).max(SITE_MEDIA_MAX_BYTES),
    hash: z.string().regex(/^[a-f0-9]{64}$/),
  })
  .strict();
export type SiteAsset = z.infer<typeof SiteAssetSchema>;

const EmbeddedImage = z
  .string()
  .max(1400000)
  .regex(/^data:image\/(?:png|jpeg|webp);base64,[A-Za-z0-9+/]+={0,2}$/)
  .refine((value) => {
    const comma = value.indexOf(",");
    const encoded = value.slice(comma + 1);
    if (encoded.length % 4 !== 0) return false;
    try {
      const head = atob(encoded.slice(0, 24));
      if (value.startsWith("data:image/png;")) return head.startsWith("\x89PNG\r\n\x1a\n");
      if (value.startsWith("data:image/jpeg;")) return head.startsWith("\xff\xd8\xff");
      return head.startsWith("RIFF") && head.slice(8, 12) === "WEBP";
    } catch {
      return false;
    }
  }, "Embedded image format does not match its bytes");

export const SiteBlockSchema = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal("hero"),
      id: BlockId,
      eyebrow: z.string().max(80).optional(),
      heading: Heading,
      body: Text.optional(),
      actions: z.array(SiteLinkSchema).max(2).default([]),
    })
    .strict(),
  z
    .object({ kind: z.literal("text"), id: BlockId, heading: Heading.optional(), body: Text })
    .strict(),
  z
    .object({
      kind: z.literal("features"),
      id: BlockId,
      heading: Heading.optional(),
      items: z
        .array(z.object({ heading: Heading, body: Text }).strict())
        .min(1)
        .max(12),
    })
    .strict(),
  z
    .object({
      kind: z.literal("metrics"),
      id: BlockId,
      heading: Heading.optional(),
      items: z
        .array(z.object({ value: z.string().min(1).max(80), label: Heading }).strict())
        .min(1)
        .max(6),
    })
    .strict(),
  z
    .object({
      kind: z.literal("image"),
      id: BlockId,
      src: z.union([EmbeddedImage, SiteMediaPathSchema]),
      alt: z.string().max(500),
      caption: z.string().max(1000).optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal("video"),
      id: BlockId,
      src: SiteMediaPathSchema,
      poster: z.union([EmbeddedImage, SiteMediaPathSchema]).optional(),
      caption: z.string().max(1000).optional(),
      label: Heading,
    })
    .strict(),
  z
    .object({
      kind: z.literal("table"),
      id: BlockId,
      heading: Heading.optional(),
      columns: z.array(z.string().min(1).max(100)).min(1).max(8),
      rows: z.array(z.array(z.string().max(2000)).min(1).max(8)).max(100),
    })
    .strict(),
  z
    .object({
      kind: z.literal("faq"),
      id: BlockId,
      heading: Heading.optional(),
      items: z
        .array(z.object({ question: Heading, answer: Text }).strict())
        .min(1)
        .max(20),
    })
    .strict(),
  z
    .object({
      kind: z.literal("cta"),
      id: BlockId,
      heading: Heading,
      body: Text.optional(),
      action: SiteLinkSchema,
    })
    .strict(),
]);
export type SiteBlock = z.infer<typeof SiteBlockSchema>;

const FormulaOperator = z.enum([
  "add",
  "subtract",
  "multiply",
  "divide",
  "min",
  "max",
  "power",
  "round",
  "abs",
]);
export const SiteFormulaTokenSchema = z.union([
  z.number().min(-1e12).max(1e12),
  z.object({ input: BlockId }).strict(),
  FormulaOperator,
]);
export type SiteFormulaToken = z.infer<typeof SiteFormulaTokenSchema>;

const CalculatorFieldSchema = z
  .object({
    id: BlockId,
    label: Heading,
    min: z.number().min(-1e12).max(1e12),
    max: z.number().min(-1e12).max(1e12),
    value: z.number().min(-1e12).max(1e12),
    step: z.number().min(1e-8).max(1e12).optional(),
  })
  .strict()
  .superRefine((field, ctx) => {
    if (field.min >= field.max || field.value < field.min || field.value > field.max)
      ctx.addIssue({ code: "custom", message: "Input bounds must contain the initial value" });
    if (field.step !== undefined) {
      const steps = (field.value - field.min) / field.step;
      if (Math.abs(steps - Math.round(steps)) > 1e-7 * Math.max(1, Math.abs(steps)))
        ctx.addIssue({ code: "custom", message: "Initial value must match the input step" });
    }
  });

export const SiteToolBlockSchema = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal("calculator"),
      id: BlockId,
      heading: Heading,
      body: Text.optional(),
      inputs: z.array(CalculatorFieldSchema).min(1).max(8),
      outputs: z
        .array(
          z
            .object({
              label: Heading,
              expression: z.array(SiteFormulaTokenSchema).min(1).max(128),
              decimals: z.number().int().min(0).max(6).default(2),
              prefix: z.string().max(40).optional(),
              suffix: z.string().max(40).optional(),
            })
            .strict(),
        )
        .min(1)
        .max(6),
    })
    .strict()
    .superRefine((block, ctx) => {
      const inputs = new Set(block.inputs.map((input) => input.id));
      if (inputs.size !== block.inputs.length)
        ctx.addIssue({ code: "custom", message: "Calculator input ids must be unique" });
      for (const [index, output] of block.outputs.entries()) {
        let depth = 0;
        for (const token of output.expression) {
          if (typeof token === "number") depth++;
          else if (typeof token === "object") {
            if (!inputs.has(token.input))
              ctx.addIssue({
                code: "custom",
                path: ["outputs", index],
                message: "Unknown calculator input",
              });
            depth++;
          } else if (token === "abs" || token === "round") {
            if (depth < 1) depth = -1000;
          } else depth--;
          if (depth < 1) {
            ctx.addIssue({
              code: "custom",
              path: ["outputs", index],
              message: "Invalid formula stack",
            });
            break;
          }
        }
        if (depth !== 1)
          ctx.addIssue({
            code: "custom",
            path: ["outputs", index],
            message: "Formula must produce exactly one result",
          });
      }
    }),
  z
    .object({
      kind: z.literal("data-table"),
      id: BlockId,
      heading: Heading,
      columns: z.array(z.string().min(1).max(100)).min(1).max(8),
      rows: z
        .array(
          z
            .array(z.union([z.string().max(2000), z.number().min(-1e15).max(1e15)]))
            .min(1)
            .max(8),
        )
        .max(200),
    })
    .strict()
    .superRefine((block, ctx) => {
      if (block.rows.some((row) => row.length !== block.columns.length))
        ctx.addIssue({ code: "custom", message: "Table rows must match columns" });
    }),
  z
    .object({
      kind: z.literal("chart"),
      id: BlockId,
      heading: Heading,
      labels: z.array(z.string().min(1).max(100)).min(1).max(40),
      series: z
        .array(
          z
            .object({
              label: Heading,
              values: z.array(z.number().min(0).max(1e12)).min(1).max(40),
            })
            .strict(),
        )
        .min(1)
        .max(6),
      unit: z.string().max(40).optional(),
    })
    .strict()
    .superRefine((block, ctx) => {
      if (block.series.some((series) => series.values.length !== block.labels.length))
        ctx.addIssue({ code: "custom", message: "Chart series must match the labels" });
      if (new Set(block.series.map((series) => series.label)).size !== block.series.length)
        ctx.addIssue({ code: "custom", message: "Chart series labels must be unique" });
    }),
]);
export type SiteToolBlock = z.infer<typeof SiteToolBlockSchema>;

const documentFields = {
  version: z.literal(SITE_DOCUMENT_VERSION),
  language: z.enum(["uk", "en", "es"]),
  title: Heading,
  appearance: z.enum(["light", "dark", "system"]).default("light"),
  typography: z.enum(["sans", "editorial"]).default("sans"),
  layout: z.enum(["wide", "reading", "centered"]).default("wide"),
  motion: z.enum(["none", "subtle"]).default("none"),
  assets: z.array(SiteAssetSchema).max(32).optional(),
};
const pageFields = {
  path: SitePathSchema,
  title: Heading,
  description: z.string().min(1).max(500),
};
const StaticPageSchema = z
  .object({
    ...pageFields,
    blocks: z.array(SiteBlockSchema).min(1).max(32),
  })
  .strict();
const ToolsPageSchema = z
  .object({
    ...pageFields,
    blocks: z
      .array(z.union([SiteBlockSchema, SiteToolBlockSchema]))
      .min(1)
      .max(32),
  })
  .strict();

type StructuredDocument = {
  assets?: SiteAsset[];
  pages: { path: string; blocks: (SiteBlock | SiteToolBlock)[] }[];
};
function validateStructuredDocument(value: StructuredDocument, ctx: z.RefinementCtx) {
  const paths = new Set(value.pages.map((page) => page.path));
  const assets = new Map((value.assets ?? []).map((asset) => [asset.id, asset]));
  if (assets.size !== (value.assets?.length ?? 0))
    ctx.addIssue({ code: "custom", message: "Asset ids must be unique" });
  if ((value.assets ?? []).reduce((size, asset) => size + asset.size, 0) > 200 * 1024 * 1024)
    ctx.addIssue({ code: "custom", message: "Site media exceeds the release limit" });
  if (!paths.has("/") || paths.size !== value.pages.length)
    ctx.addIssue({ code: "custom", message: "A unique home page and unique paths are required" });
  for (const [index, page] of value.pages.entries()) {
    if (page.path === "/media" || page.path.startsWith("/media/"))
      ctx.addIssue({
        code: "custom",
        path: ["pages", index],
        message: "Media routes are reserved",
      });
    if (page.blocks.filter((block) => block.kind === "hero").length > 1)
      ctx.addIssue({
        code: "custom",
        path: ["pages", index],
        message: "Only one page hero is allowed",
      });
    const ids = new Set(page.blocks.map((block) => block.id));
    if (ids.size !== page.blocks.length)
      ctx.addIssue({ code: "custom", path: ["pages", index], message: "Block ids must be unique" });
    for (const block of page.blocks) {
      if (block.kind === "table" && block.rows.some((row) => row.length !== block.columns.length))
        ctx.addIssue({
          code: "custom",
          path: ["pages", index],
          message: "Table rows must match columns",
        });
      const links =
        block.kind === "hero" ? block.actions : block.kind === "cta" ? [block.action] : [];
      for (const link of links) {
        if (
          (link.href.startsWith("#") && !ids.has(link.href.slice(1))) ||
          (link.href.startsWith("/") && !paths.has(link.href))
        )
          ctx.addIssue({
            code: "custom",
            path: ["pages", index],
            message: "Link target is missing",
          });
      }
      const refs =
        block.kind === "image"
          ? [[block.src, "image/"]]
          : block.kind === "video"
            ? [[block.src, "video/"], ...(block.poster ? [[block.poster, "image/"]] : [])]
            : [];
      for (const [src, type] of refs) {
        if (src!.startsWith("/media/") && !assets.get(src!.slice(7))?.mimeType.startsWith(type!))
          ctx.addIssue({
            code: "custom",
            path: ["pages", index],
            message: "Local media must match an asset and its type",
          });
      }
    }
  }
}

export const StaticSiteDocumentSchema = z
  .object({
    ...documentFields,
    profile: z.literal("static"),
    pages: z.array(StaticPageSchema).min(1).max(20),
  })
  .strict()
  .superRefine(validateStructuredDocument);
export type StaticSiteDocument = z.infer<typeof StaticSiteDocumentSchema>;
export const ToolsSiteDocumentSchema = z
  .object({
    ...documentFields,
    profile: z.literal("tools"),
    pages: z.array(ToolsPageSchema).min(1).max(20),
  })
  .strict()
  .superRefine(validateStructuredDocument);
export type ToolsSiteDocument = z.infer<typeof ToolsSiteDocumentSchema>;
export const PublishableSiteDocumentSchema = z.union([
  StaticSiteDocumentSchema,
  ToolsSiteDocumentSchema,
]);
export type PublishableSiteDocument = z.infer<typeof PublishableSiteDocumentSchema>;
// Validate new visible content without rewriting stored history, opaque ids or image bytes.
// Перевіряємо новий видимий вміст без переписування історії, ідентифікаторів чи зображень.
export const PublishableSiteDocumentWriteSchema = PublishableSiteDocumentSchema.superRefine(
  (document, ctx) => {
    const opaque = new Set(["id", "assets", "expression", "src", "poster"]);
    const visit = (value: unknown, path: (string | number)[]) => {
      if (typeof value === "string" && !currentProductAddress(value))
        ctx.addIssue({ code: "custom", path, message: retiredAddressMessage });
      else if (Array.isArray(value))
        for (const [index, item] of value.entries()) visit(item, [...path, index]);
      else if (value && typeof value === "object")
        for (const [key, item] of Object.entries(value))
          if (!opaque.has(key)) visit(item, [...path, key]);
    };
    visit(document, []);
  },
);

export const InteractiveSiteDocumentSchema = z
  .object({
    version: z.literal(SITE_DOCUMENT_VERSION),
    profile: z.literal("interactive"),
    language: z.enum(["uk", "en", "es"]),
    title: Heading,
    html: z.string().min(1).max(SITE_INTERACTIVE_MAX_LENGTH),
  })
  .strict();
export type InteractiveSiteDocument = z.infer<typeof InteractiveSiteDocumentSchema>;
export const SiteDocumentSchema = z.union([
  StaticSiteDocumentSchema,
  ToolsSiteDocumentSchema,
  InteractiveSiteDocumentSchema,
]);
export type SiteDocument = z.infer<typeof SiteDocumentSchema>;
